import React from 'react';
import { Link, useParams } from 'react-router-dom';
import PageHero from '../components/PageHero';
import { services, brand } from '../data/mock';
import { ArrowRight, ChevronRight, Phone, Mail, MessageCircle, CheckCircle2, BadgeCheck } from 'lucide-react';

export default function ServiceDetail() {
  const { slug } = useParams();
  const service = services.find((s) => s.slug === slug) || services[0];
  const currentIndex = services.findIndex((s) => s.slug === service.slug);
  const nextService = services[(currentIndex + 1) % services.length];

  return (
    <div>
      <PageHero
        title={service.title}
        subtitle={service.short}
        image={service.image}
        imgPos={service.imgPos}
        imgFit={service.imgFit}
        imgBg={service.imgBg}
        breadcrumbs={[{ label: 'Services', to: '/services' }, { label: service.title }]}
      />

      <section className="py-24 bg-white">
        <div className="max-w-[1300px] mx-auto px-6 lg:px-8 grid grid-cols-1 lg:grid-cols-[300px_1fr] gap-10">
          {/* Sidebar */}
          <aside className="lg:sticky lg:top-24 self-start space-y-6">
            <div className="bg-slate-50 rounded-2xl p-2 border border-slate-100">
              <div className="px-4 py-3 text-[#0A2540] font-bold uppercase tracking-wider text-xs border-b border-slate-200">All Services</div>
              <ul className="py-2">
                {services.map((s) => (
                  <li key={s.slug}>
                    <Link
                      to={`/services/${s.slug}`}
                      className={`flex items-center justify-between gap-2 px-4 py-3 text-sm rounded-lg transition-colors ${
                        s.slug === slug ? 'bg-[#F5B800] text-[#0A2540] font-semibold' : 'text-slate-700 hover:bg-white hover:text-[#0A2540]'
                      }`}
                    >
                      <span className="flex items-center gap-2">
                        {s.title}
                        {s.isNew && <span className={`text-[9px] font-bold px-1.5 py-0.5 rounded uppercase tracking-widest ${s.slug === slug ? 'bg-[#0A2540] text-white' : 'bg-[#F5B800]/20 text-[#0A2540]'}`}>New</span>}
                      </span>
                      <ChevronRight size={14} />
                    </Link>
                  </li>
                ))}
              </ul>
            </div>

            <div className="bg-slate-50 border border-slate-100 text-slate-900 rounded-2xl p-6 relative overflow-hidden">
              <div className="relative">
                <div className="w-12 h-12 rounded-xl bg-[#F5B800] text-[#0A2540] flex items-center justify-center mb-4"><Phone size={20} /></div>
                <h4 className="font-bold text-lg mb-1">Talk to an expert</h4>
                <p className="text-slate-600 text-sm mb-5">Free proposal within 72 hours.</p>
                <a href={`tel:${brand.phone}`} className="btn-cta block text-center font-semibold py-2.5 rounded-lg mb-2">
                  {brand.phone}
                </a>
                <a href={`https://wa.me/${brand.whatsapp}`} target="_blank" rel="noreferrer" className="flex items-center justify-center gap-2 bg-[#25D366] hover:bg-[#1EBE5D] text-white text-sm font-semibold py-2.5 rounded-lg transition-colors mb-2">
                  <MessageCircle size={14} /> WhatsApp
                </a>
                <a href={`mailto:${brand.email}`} className="flex items-center justify-center gap-2 text-slate-600 hover:text-[#0A2540] text-sm py-2 transition-colors">
                  <Mail size={14} /> {brand.email}
                </a>
              </div>
            </div>
          </aside>

          {/* Content */}
          <div>
            <div className="max-w-3xl mb-10">
              <h2 className="text-3xl md:text-4xl font-bold text-slate-900 tracking-tight mb-4">Overview</h2>
              <p className="text-slate-600 leading-relaxed text-[16px]">
                {service.short} Delivered by Durga Facility Management with 30+ years of accumulated expertise, ISO-aligned processes and a background-verified workforce across Karnataka, Goa, Maharashtra, Tamil Nadu and Telangana.
              </p>
            </div>

            {/* Highlights grid */}
            <div className="mb-14">
              <h3 className="text-xl font-bold text-slate-900 mb-6 tracking-tight">What&rsquo;s included</h3>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                {service.highlights.map((h, i) => (
                  <div key={i} className="flex items-center gap-3 p-4 bg-slate-50 rounded-xl border border-slate-100">
                    <div className="w-9 h-9 rounded-lg bg-[#0A2540] text-white flex items-center justify-center shrink-0">
                      <CheckCircle2 size={18} />
                    </div>
                    <div className="font-medium text-slate-800 text-[15px]">{h}</div>
                  </div>
                ))}
              </div>
            </div>

            {/* Feature image / video */}
            {service.video ? (
              <div className="rounded-2xl overflow-hidden aspect-[16/9] mb-14 shadow-lg bg-slate-900 relative">
                <video
                  src={service.video}
                  className="w-full h-full object-cover"
                  controls
                  playsInline
                  preload="metadata"
                  poster={service.image}
                />
                <div className="absolute top-4 left-4 bg-[#F5B800] text-[#0A2540] text-[11px] font-bold uppercase tracking-widest px-3 py-1 rounded-full pointer-events-none">
                  Watch · Drone in action
                </div>
              </div>
            ) : (
              <div className="rounded-2xl overflow-hidden aspect-[16/9] mb-14 shadow-lg bg-slate-100" style={service.imgBg ? { background: service.imgBg } : undefined}>
                <img src={service.image} alt={service.title} style={{ objectPosition: service.imgPos || 'center', objectFit: service.imgFit || 'contain' }} className="w-full h-full" />
              </div>
            )}

            {/* Why Durga for this service */}
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-14">
              {[
                { title: 'Trained workforce', desc: 'Every professional deployed for this service is trained on equipment, safety and SOPs.' },
                { title: 'Modern equipment', desc: 'From ride-on scrubbers to drone systems, we bring the right tool for every job.' },
                { title: 'Auditable SLAs', desc: 'Monthly reviews, digital reporting and clear escalation paths keep quality high.' },
              ].map((b) => (
                <div key={b.title} className="p-6 rounded-2xl bg-white border border-slate-100 hover:shadow-lg transition-shadow">
                  <BadgeCheck className="text-[#0A2540] mb-3" size={22} />
                  <h4 className="font-bold text-slate-900 mb-2">{b.title}</h4>
                  <p className="text-sm text-slate-600 leading-relaxed">{b.desc}</p>
                </div>
              ))}
            </div>

            {/* Bottom CTA */}
            <div className="bg-slate-50 border border-slate-100 rounded-2xl p-8 md:p-10 relative overflow-hidden mb-8">
              <div className="relative grid grid-cols-1 md:grid-cols-[1.5fr_1fr] gap-6 items-center">
                <div>
                  <h3 className="text-2xl md:text-3xl font-bold leading-tight tracking-tight text-slate-900">Ready to see {service.title.toLowerCase()} in action at your facility?</h3>
                  <p className="text-slate-600 mt-3">Book a free on-site audit and receive a tailored proposal within 72 hours.</p>
                </div>
                <Link to="/contact" className="btn-cta inline-flex items-center justify-center gap-2 px-6 py-3.5 rounded-lg font-semibold">
                  Request a Quote <ArrowRight size={16} />
                </Link>
              </div>
            </div>

            {/* Next service */}
            <Link to={`/services/${nextService.slug}`} className="group flex items-center justify-between p-6 rounded-2xl border border-slate-200 hover:border-slate-300 hover:bg-slate-50 transition-all">
              <div>
                <div className="text-xs text-slate-500 uppercase tracking-widest">Next service</div>
                <div className="font-bold text-slate-900 group-hover:text-[#0A2540] transition-colors mt-1">{nextService.title}</div>
              </div>
              <ArrowRight size={20} className="text-[#0A2540] group-hover:translate-x-1 transition-transform" />
            </Link>
          </div>
        </div>
      </section>
    </div>
  );
}
